/**
 * IoT device-page honesty: a model-listed IMEI is not a device.
 *
 * GET /iot/devices/:id already resolves deviceId then metadata.imei (and
 * siblings). A 404 means no in-scope device document — not a transport
 * crash. Do not invent or register a device to make the 404 go away.
 */

import { CpHttpError } from '../../lib/cpClient';

export function isMissingIotDevice(error: unknown): boolean {
  if (!error) return false;
  if (error instanceof CpHttpError) {
    return error.status === 404;
  }
  if (typeof error === 'object' && error !== null && 'status' in error) {
    return Number((error as { status?: unknown }).status) === 404;
  }
  const message = error instanceof Error ? error.message : String(error);
  const detail =
    typeof error === 'object' && error !== null && 'detail' in error
      ? String((error as { detail?: unknown }).detail || '')
      : '';
  const haystack = `${message} ${detail}`;
  return /\bHTTP 404\b/.test(haystack) || /device not found/i.test(haystack);
}

export function missingIotDeviceCopy(identifier: string): {
  title: string;
  body: string;
} {
  const id = String(identifier || '').trim() || 'this identifier';
  return {
    title: 'Not a registered device',
    body:
      `${id} is not a registered device for this app. ` +
      'A model-listed IMEI is not a device until it is registered here. ' +
      'This page does not create one.',
  };
}

/**
 * Device-page href from a model instance. Use the document's deviceId only.
 * Do not fall back to metadata.imei — that is the FMT100 404 (IMEI used as id).
 */
export function deviceDetailPath(device: {
  deviceId?: string | null;
  metadata?: { imei?: string | null } | null;
}): string | null {
  const id = String(device?.deviceId || '').trim();
  if (!id) return null;
  return `/iotshack/devices/${encodeURIComponent(id)}`;
}
